import { create } from 'zustand';
import { supabase } from '@/lib/supabase/client';
import type { Contact, ContactFormData } from '@/types/crm';

interface ContactStore {
  contacts: Contact[];
  selectedContact: Contact | null;
  isLoading: boolean;
  searchTerm: string;
  companyFilter: string | null;
  fetchContacts: () => Promise<void>;
  fetchContactById: (id: string) => Promise<Contact | null>;
  fetchContactsByCompany: (companyId: string) => Promise<Contact[]>;
  createContact: (data: ContactFormData) => Promise<Contact | null>;
  updateContact: (id: string, data: Partial<ContactFormData>) => Promise<void>;
  deleteContact: (id: string) => Promise<void>;
  setSelectedContact: (contact: Contact | null) => void;
  setSearchTerm: (term: string) => void;
  setCompanyFilter: (companyId: string | null) => void;
}

export const useContactStore = create<ContactStore>((set, get) => ({
  contacts: [],
  selectedContact: null,
  isLoading: false,
  searchTerm: '',
  companyFilter: null,

  fetchContacts: async () => {
    set({ isLoading: true });
    try {
      const { searchTerm, companyFilter } = get();
      let query = supabase
        .from('crm_contacts')
        .select(`
          *,
          crm_companies!crm_contacts_company_id_fkey (name)
        `)
        .order('first_name', { ascending: true });

      if (companyFilter) {
        query = query.eq('company_id', companyFilter);
      }

      if (searchTerm) {
        query = query.or(`first_name.ilike.%${searchTerm}%,last_name.ilike.%${searchTerm}%,email.ilike.%${searchTerm}%`);
      }

      const { data, error } = await query;
      if (error) throw error;
      
      const contacts = (data || []).map((c: Record<string, unknown>) => ({
        ...c,
        company_name: (c.crm_companies as { name?: string } | null)?.name,
      })) as Contact[];
      
      set({ contacts });
    } finally {
      set({ isLoading: false });
    }
  },

  fetchContactById: async (id: string) => {
    const { data, error } = await supabase
      .from('crm_contacts')
      .select(`
        *,
        crm_companies!crm_contacts_company_id_fkey (name)
      `)
      .eq('id', id)
      .single();
    
    if (error) return null;
    const contact = {
      ...data,
      company_name: data.crm_companies?.name,
    } as Contact;
    set({ selectedContact: contact });
    return contact;
  },

  fetchContactsByCompany: async (companyId: string) => {
    const { data, error } = await supabase
      .from('crm_contacts')
      .select('*')
      .eq('company_id', companyId)
      .order('is_primary', { ascending: false });
    
    if (error) return [];
    return data || [];
  },

  createContact: async (data: ContactFormData) => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return null;

    const { data: contact, error } = await supabase
      .from('crm_contacts')
      .insert({
        ...data,
        created_by: user.id,
      })
      .select()
      .single();

    if (error) throw error;
    await get().fetchContacts();
    return contact;
  },

  updateContact: async (id: string, data: Partial<ContactFormData>) => {
    const { error } = await supabase
      .from('crm_contacts')
      .update(data)
      .eq('id', id);

    if (error) throw error;
    await get().fetchContacts();
  },

  deleteContact: async (id: string) => {
    const { error } = await supabase
      .from('crm_contacts')
      .delete()
      .eq('id', id);

    if (error) throw error;
    await get().fetchContacts();
  },

  setSelectedContact: (contact: Contact | null) => set({ selectedContact: contact }),
  setSearchTerm: (term: string) => set({ searchTerm: term }),
  setCompanyFilter: (companyId: string | null) => set({ companyFilter: companyId }),
}));
